import { useEffect, useMemo, useState } from "react";
import FormInput from "../../molecules/FormInput";
import FormTemplate from "../../templates/FormTemplate";
import {
  Button,
  Divider,
  Grid,
  Input,
  InputBase,
  Typography,
} from "@mui/material";
import { ImportService } from "../../../services/api";
import useRefresh from "../../../hooks/useRefresh";
import { DropDownItem } from "../../molecules/Dropdown";
import { capitalizeFirstLetter } from "../../../helpers/string";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import FieldLabel from "../../atoms/FieldLabel";
import {
  HttpHeader,
  ImportableModel,
  ImportMapping,
} from "../../../services/swagger";
import HttpHeaderList from "../../molecules/HttpHeaderList";
import useConfirm from "../../../hooks/useConfirm";

export default function ImportSettingsPage() {
  const { t } = useTranslation("ImportSettings");
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [model, setModel] = useState<ImportableModel>(ImportableModel.Product);
  const [autoSync, setAutoSync] = useState(false);
  const [mappingList, setMapping] = useState<ImportMapping[]>([]);
  const [headers, setHeaders] = useState<HttpHeader[]>([]);
  const [url, setUrl] = useState("");

  const { mutate: loadImportConfig, isPending: isLoading } = useMutation({
    mutationFn: async () => {
      const res = await ImportService.getImportConfig({ model });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (!data) return;
      setAutoSync(data.autoSync);
      setHeaders(data.headers);
      setMapping(data.mapping);
      setUrl(data.url);
    },
  });

  const { mutate: saveImportConfig, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      const res = await ImportService.updateImportConfig({
        model,
        partialImportConfig: {
          autoSync,
          headers,
          mapping: mappingList,
          url,
        },
      });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (!data) return;
      setAutoSync(data.autoSync);
      setHeaders(data.headers);
      setMapping(data.mapping);
      setUrl(data.url);
    },
  });

  const { mutate: synchronize, isPending: isSynchronizing } = useMutation({
    mutationFn: async () => {
      const res = await ImportService.createImportSynchronization({
        model,
      });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (!data) return;
    },
  });

  const handleSubmit = async () => {
    saveImportConfig();
  };

  const handleSynchronize = async () => {
    confirm(synchronize);
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleMappingChange = (index: number, newTargetPath: string) => {
    setMapping((prevMapping) =>
      prevMapping.map((item, i) =>
        i === index ? { ...item, targetPath: newTargetPath } : item,
      ),
    );
  };

  const modelList: DropDownItem[] = useMemo(() => {
    return Object.values(ImportableModel).map((key) => ({
      name: capitalizeFirstLetter(key),
      value: key,
    }));
  }, []);

  useEffect(() => {
    if (model) loadImportConfig();
  }, [model]);

  const loading = isLoading || isSaving || isSynchronizing;

  return (
    <FormTemplate
      title={t("ImportConfiguration")}
      disableCancelButton
      submitButtonText={t("Save")}
      disableEnter
      onSubmit={handleSubmit}
      onCancel={handleClose}
      confirm
      loading={loading}
    >
      <Grid container direction={"column"} rowGap={3}>
        <Grid container alignItems={"center"} rowGap={3}>
          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("Model")} />
          </Grid>
          <Grid size={{ xs: 7, md: 4 }}>
            <FormInput
              loading={loading}
              field="configName"
              label={""}
              type="list"
              placeholder="None"
              value={model}
              list={modelList}
              onChange={(value) => setModel(value as ImportableModel)}
              required
            />
          </Grid>
          <Grid size={{ xs: 0, md: 4 }}></Grid>

          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("AutoSync")} />
          </Grid>
          <Grid size={{ xs: 2, md: 2 }}>
            <FormInput
              loading={loading}
              field="autoSync"
              label={""}
              type="switch"
              value={autoSync}
              onChange={(value) => setAutoSync(value as boolean)}
            />
          </Grid>
          <Grid
            size={{ xs: 5, md: 2 }}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              variant="contained"
              onClick={handleSynchronize}
              loading={loading}
            >
              {t("Synchronize")}
            </Button>
          </Grid>
          <Grid size={{ xs: 0, md: 4 }} />

          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("Url")} />
          </Grid>
          <Grid size={{ xs: 7, md: 4 }}>
            <FormInput
              loading={loading}
              field="url"
              label={""}
              type="string"
              value={url}
              onChange={(value) => setUrl(value as string)}
            />
          </Grid>
          <Grid size={{ xs: 0, md: 4 }}></Grid>

          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>

          <Grid size={12}>
            <HttpHeaderList
              onChange={(newHeaders) => setHeaders(newHeaders)}
              value={headers}
              loading={loading}
            />
          </Grid>

          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("KeyPath")} />
          </Grid>

          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("TargetPath")} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>
          <Grid container size={{ xs: 12 }} rowGap={1}>
            {mappingList.map((mapping, i) => (
              <Grid
                container
                size={12}
                key={`${model}-${mapping.keyPath}-${i}`}
              >
                <Grid size={{ xs: 5, md: 4 }}>
                  <Typography>{mapping.keyPath}</Typography>
                </Grid>
                <Grid size={{ xs: 7, md: 4 }}>
                  <InputBase
                    fullWidth
                    value={mapping.targetPath ?? ""}
                    type="text"
                    placeholder={mapping.keyPath}
                    onChange={(e) => handleMappingChange(i, e.target.value)}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>
        </Grid>
      </Grid>
    </FormTemplate>
  );
}
